import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { couponService } from '../../services/couponService';
import './CouponInput.css';

export default function CouponInput({
  subtotal,
  shippingCost,
  onCouponApplied,
  onCouponRemoved,
}) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [revalidating, setRevalidating] = useState(false);
  const prevSubtotal = useRef(subtotal);
  const prevShipping = useRef(shippingCost);
  const initialFetchDone = useRef(false);

  // Check for existing active reservation on mount
  useEffect(() => {
    if (initialFetchDone.current) return;
    initialFetchDone.current = true;

    const fetchActive = async () => {
      try {
        const response = await couponService.getActive();
        if (response.data && response.data.reservationId) {
          setAppliedCoupon(response.data);
          setCode(response.data.couponCode);
          onCouponApplied?.(response.data);
        }
      } catch {
        // No active coupon
      }
    };
    fetchActive();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-validate coupon when subtotal or shipping changes
  const revalidate = useCallback(async () => {
    if (!appliedCoupon?.couponCode) return;

    setRevalidating(true);
    try {
      const response = await couponService.validate(appliedCoupon.couponCode, subtotal, shippingCost);

      if (response.success && response.data?.valid) {
        const updated = {
          ...appliedCoupon,
          totalDiscount: response.data.totalDiscount,
          discountBreakdown: response.data.discountBreakdown,
        };
        setAppliedCoupon(updated);
        onCouponApplied?.(updated);
        setError(null);
      } else {
        // Coupon no longer valid - auto-remove
        try {
          await couponService.remove(appliedCoupon.reservationId);
        } catch { /* best effort */ }
        setAppliedCoupon(null);
        setCode('');
        onCouponRemoved?.();
        setError(response.data?.message || 'Coupon is no longer valid for this order');
      }
    } catch {
      // Validation failed - keep existing coupon but warn
      setError('Could not re-validate coupon. Discount may change at checkout.');
    } finally {
      setRevalidating(false);
    }
  }, [appliedCoupon, subtotal, shippingCost, onCouponApplied, onCouponRemoved]);

  useEffect(() => {
    const subtotalChanged = prevSubtotal.current !== subtotal;
    const shippingChanged = prevShipping.current !== shippingCost;
    prevSubtotal.current = subtotal;
    prevShipping.current = shippingCost;

    if ((subtotalChanged || shippingChanged) && appliedCoupon) {
      revalidate();
    }
  }, [subtotal, shippingCost, appliedCoupon, revalidate]);

  // Reservation expiry countdown
  useEffect(() => {
    if (!appliedCoupon?.expiresAt) return;

    const checkExpiry = () => {
      const expiresAt = new Date(appliedCoupon.expiresAt);
      if (new Date() >= expiresAt) {
        setAppliedCoupon(null);
        setCode('');
        onCouponRemoved?.();
        setError('Coupon reservation expired. Please re-apply.');
      }
    };

    const interval = setInterval(checkExpiry, 30000); // Check every 30s
    return () => clearInterval(interval);
  }, [appliedCoupon?.expiresAt, onCouponRemoved]);

  const handleApply = async () => {
    const trimmed = code.trim();
    if (!trimmed) {
      setError('Please enter a coupon code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await couponService.apply(trimmed, subtotal, shippingCost);

      if (response.success && response.data) {
        setAppliedCoupon(response.data);
        onCouponApplied?.(response.data);
      } else {
        setError(response.message || 'Failed to apply coupon');
      }
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.data?.message ||
        err.message ||
        'Failed to apply coupon';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async () => {
    if (!appliedCoupon?.reservationId) return;

    setLoading(true);
    setError(null);

    try {
      await couponService.remove(appliedCoupon.reservationId);
      setAppliedCoupon(null);
      setCode('');
      onCouponRemoved?.();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove coupon');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !loading && !appliedCoupon) {
      e.preventDefault();
      handleApply();
    }
  };

  const formatDiscount = (breakdown) => {
    if (!breakdown?.components?.length) return null;
    return breakdown.components.map((c) => c.description).filter(Boolean).join(' + ') || null;
  };

  const getRemainingTime = () => {
    if (!appliedCoupon?.expiresAt) return null;
    const diff = new Date(appliedCoupon.expiresAt) - new Date();
    if (diff <= 0) return null;
    const mins = Math.ceil(diff / 60000);
    return mins <= 5 ? `Expires in ${mins}m` : null;
  };

  const expiryWarning = getRemainingTime();

  return (
    <div className="coupon-section">
      <h3 className="coupon-title">Have a coupon?</h3>

      <div className="coupon-input-row">
        <input
          type="text"
          className={`coupon-input ${appliedCoupon ? 'applied' : ''}`}
          placeholder="Enter coupon code"
          value={code}
          onChange={(e) => {
            setCode(e.target.value.toUpperCase());
            setError(null);
          }}
          onKeyDown={handleKeyDown}
          disabled={loading || revalidating || !!appliedCoupon}
          maxLength={50}
        />
        {appliedCoupon ? (
          <button
            className="coupon-btn coupon-btn-remove"
            onClick={handleRemove}
            disabled={loading || revalidating}
          >
            {loading ? <span className="coupon-spinner" /> : 'Remove'}
          </button>
        ) : (
          <button
            className="coupon-btn coupon-btn-apply"
            onClick={handleApply}
            disabled={loading || !code.trim()}
          >
            {loading ? <span className="coupon-spinner" /> : 'Apply'}
          </button>
        )}
      </div>

      <AnimatePresence mode="wait">
        {error && (
          <motion.div
            className="coupon-error"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
          >
            {error}
          </motion.div>
        )}

        {appliedCoupon && (
          <motion.div
            className="coupon-success"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
          >
            <div className="coupon-success-header">
              <span className="coupon-check-icon">&#10003;</span>
              <span className="coupon-applied-label">
                {appliedCoupon.couponName || appliedCoupon.couponCode}
              </span>
              {revalidating && <span className="coupon-revalidating">Updating...</span>}
            </div>
            {appliedCoupon.discountBreakdown && (
              <div className="coupon-discount-detail">
                {formatDiscount(appliedCoupon.discountBreakdown)}
              </div>
            )}
            <div className="coupon-savings">
              You save{' '}
              <strong>
                ₹{parseFloat(appliedCoupon.totalDiscount || 0).toLocaleString()}
              </strong>
            </div>
            {expiryWarning && (
              <div className="coupon-expiry-warning">{expiryWarning}</div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
